import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, LinearProgress, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, CloudUpload as CloudUploadIcon, Code as CodeIcon,
  ContentCopy as ContentCopyIcon, DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  PlayCircleOutline as PlayCircleOutlineIcon, Refresh as RefreshIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Toaster } from 'modules/platform-components/Toaster';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import { hasPermission } from 'modules/platform-components/utils/permission.service';
import {
  apiPaths, deleteData, get, postData,
} from 'modules/platform-components/api-integration-util';
// Reused rather than reimplemented, so "Copy embed HTML" here and the editor's
// video button can never produce different markup for the same URL.
import { mediaUrlToEmbedHtml } from 'modules/platform-components/jodit/jodit-media-embed';

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

// Mirrors media.upload.allowed-extensions / media.upload.max-size-mb in
// QuestionBankApp. Duplicated deliberately: the server is the authority and
// re-checks everything, but a 100 MB upload that only fails after the bytes are
// on the wire is a terrible way to learn the file was the wrong type.
const ALLOWED_EXTENSIONS = ['mp4', 'm4v', 'webm', 'ogv', 'mov', 'mp3', 'm4a', 'aac', 'wav', 'oga'];
const MAX_SIZE_MB = 100;

const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'aac', 'wav', 'oga'];

const extensionOf = (name) => {
  const clean = (name || '').split('#')[0].split('?')[0];
  const dot = clean.lastIndexOf('.');
  const slash = clean.lastIndexOf('/');
  return dot > slash ? clean.slice(dot + 1).toLowerCase() : '';
};

const isAudio = (name) => AUDIO_EXTENSIONS.indexOf(extensionOf(name)) !== -1;

/**
 * Labels arrive display-ready from the server.
 *
 * Deliberately NOT de-hyphenated here any more: an edited label is stored verbatim
 * in media_assets, so hyphens in it are ones the user typed. The server un-slugifies
 * only the fallback it parses out of the key, where the hyphens really are separators
 * - it is the only side that knows which case a given row is.
 */
const prettyLabel = (asset) => (asset.label || asset.fileName || '').trim();

const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDate = (epochMillis) => {
  if (!epochMillis) return '—';
  return new Date(epochMillis).toLocaleString();
};

/**
 * Clipboard with a fallback.
 *
 * navigator.clipboard is undefined on insecure origins - which includes
 * http://localhost:3000 in some browsers - and the whole point of this page is
 * copying a URL, so silently doing nothing there is not acceptable.
 */
const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // Fall through to the textarea approach.
  }
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textArea);
    return ok;
  } catch (e) {
    return false;
  }
};

export default function MediaLibrary() {
  const [assets, setAssets] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [previewKey, setPreviewKey] = useState(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFileObj, setUploadFileObj] = useState(null);
  const [uploadQuestionId, setUploadQuestionId] = useState('');
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const fileInputRef = useRef(null);

  // Edit dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editQuestionId, setEditQuestionId] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [usage, setUsage] = useState({ loading: false, questionIds: [], error: null });
  const [deleting, setDeleting] = useState(false);

  const canUpload = hasPermission('Media', 'Upload Media');
  const canDelete = hasPermission('Media', 'Delete Media');
  // Editing a label is a smaller act than publishing a new file to a public bucket,
  // so anyone who may upload may also rename. Matches the server, which gates
  // /media/update on 'Upload Media' rather than adding a fourth sub-permission that
  // no existing role would have.
  const canEdit = canUpload;

  const loadAssets = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    get(apiPaths.mediaLibraryList, {}, getHeaders())
      .then((res) => {
        setAssets(res?.result?.items || []);
        setTruncated(!!res?.result?.truncated);
      })
      .catch((e) => {
        // Don't fall through to an empty list - "no files yet" and "we could not
        // find out" look identical to the user but mean very different things.
        setLoadError(
          e?.response?.data?.msg || 'Could not load the media library. Please retry.'
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const validate = (file) => {
    const extension = extensionOf(file.name);
    if (!extension) {
      return 'That file has no extension, so its type cannot be determined.';
    }
    if (ALLOWED_EXTENSIONS.indexOf(extension) === -1) {
      return `.${extension} files are not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}.`;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `That file is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The limit is ${MAX_SIZE_MB} MB.`;
    }
    return null;
  };

  const openUploadDialog = () => {
    setUploadFileObj(null);
    setUploadQuestionId('');
    setUploadLabel('');
    setUploadPercent(0);
    setUploadOpen(true);
  };

  const chooseFile = (file) => {
    if (!file) return;
    const problem = validate(file);
    if (problem) {
      Toaster(toast.TYPE.ERROR, problem);
      return;
    }
    setUploadFileObj(file);
    // Seed the label from the file name so the field is never left blank by accident;
    // the whole point of this dialog is that the listing is readable afterwards.
    if (!uploadLabel) {
      const dot = file.name.lastIndexOf('.');
      setUploadLabel(dot > 0 ? file.name.slice(0, dot) : file.name);
    }
  };

  const submitUpload = () => {
    if (!uploadFileObj) return;

    const formData = new FormData();
    formData.append('file', uploadFileObj, uploadFileObj.name);

    const params = {};
    const trimmedId = uploadQuestionId.trim();
    if (trimmedId) params.questionId = trimmedId;
    if (uploadLabel.trim()) params.label = uploadLabel.trim();

    setUploading(true);
    setUploadPercent(0);

    // Content-Type is deliberately not set: axios's xhr adapter strips it for
    // FormData bodies so the browser can add the multipart boundary itself.
    postData(apiPaths.mediaLibraryUpload, {
      data: formData,
      params,
      headers: getHeaders(),
      onUploadProgress: (event) => {
        if (event.total) {
          setUploadPercent(Math.round((event.loaded * 100) / event.total));
        }
      },
    })
      .then((res) => {
        const asset = res?.result;
        if (asset) {
          setAssets((current) => [asset, ...current]);
          Toaster(toast.TYPE.SUCCESS, `${prettyLabel(asset)} uploaded.`);
        } else {
          loadAssets();
        }
        setUploadOpen(false);
      })
      .catch((e) => {
        // postData rethrows the backend's msg, which carries the real reason for
        // 400 (type/size) and 403 (permission).
        Toaster(toast.TYPE.ERROR, typeof e === 'string' ? e : 'Upload failed. Please try again.');
      })
      .finally(() => {
        setUploading(false);
        setUploadPercent(0);
        if (fileInputRef.current) {
          // Clear it so re-picking the same file fires onChange again.
          fileInputRef.current.value = '';
        }
      });
  };

  const openEditDialog = (asset) => {
    setEditTarget(asset);
    // Seeded from the current values so the dialog opens on what is already there and
    // an edit of one field cannot silently blank the other.
    setEditQuestionId(asset.questionId ? String(asset.questionId) : '');
    setEditLabel(prettyLabel(asset));
  };

  const submitEdit = () => {
    if (!editTarget) return;

    const trimmedLabel = editLabel.trim();
    if (!trimmedLabel) {
      Toaster(toast.TYPE.ERROR, 'Please enter a label so the file can be identified in the list.');
      return;
    }

    setSaving(true);
    postData(apiPaths.mediaLibraryUpdate, {
      params: {
        key: editTarget.key,
        // Sent as 0 rather than omitted: leaving it out would read as "don't change it",
        // and clearing the question association has to be possible.
        questionId: editQuestionId.trim() ? Number(editQuestionId.trim()) : 0,
        label: trimmedLabel,
      },
      headers: getHeaders(),
    })
      .then((res) => {
        const updated = res?.result;
        if (updated) {
          // Patch in place rather than reloading: the S3 key is unchanged, so the row
          // keeps its identity and its open preview stays open.
          setAssets((current) => current.map((a) => (a.key === updated.key ? updated : a)));
        } else {
          loadAssets();
        }
        Toaster(toast.TYPE.SUCCESS, 'Changes saved.');
        setEditTarget(null);
      })
      .catch((e) => {
        // Carries the server's real reason: unknown question id, blank or over-long
        // label, or a file that has since been deleted.
        Toaster(toast.TYPE.ERROR, typeof e === 'string' ? e : 'Could not save the changes.');
      })
      .finally(() => setSaving(false));
  };

  const openDeleteDialog = (asset) => {
    setDeleteTarget(asset);
    setUsage({ loading: true, questionIds: [], error: null });

    get(apiPaths.mediaLibraryUsage, { key: asset.key }, getHeaders())
      .then((res) => {
        setUsage({ loading: false, questionIds: res?.result || [], error: null });
      })
      .catch(() => {
        // An empty list here would read as "safe to delete" when the truth is we
        // don't know. Say so instead.
        setUsage({
          loading: false,
          questionIds: [],
          error: 'Could not check where this file is used.',
        });
      });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    deleteData(apiPaths.mediaLibraryDelete, {
      params: { key: deleteTarget.key },
      headers: getHeaders(),
    })
      .then(() => {
        setAssets((current) => current.filter((a) => a.key !== deleteTarget.key));
        if (previewKey === deleteTarget.key) setPreviewKey(null);
        Toaster(toast.TYPE.SUCCESS, 'File deleted.');
        setDeleteTarget(null);
      })
      .catch((e) => {
        Toaster(toast.TYPE.ERROR, typeof e === 'string' ? e : 'Could not delete the file.');
      })
      .finally(() => setDeleting(false));
  };

  const handleCopyUrl = async (asset) => {
    const ok = await copyText(asset.url);
    Toaster(
      ok ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
      ok ? 'URL copied. Paste it into the question editor’s video button.'
        : 'Could not copy. Select the URL and copy manually.'
    );
  };

  const handleCopyEmbed = async (asset) => {
    const ok = await copyText(mediaUrlToEmbedHtml(asset.url));
    Toaster(
      ok ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
      ok ? 'Embed HTML copied. Paste it into the editor’s Code tab.' : 'Could not copy. Please try again.'
    );
  };

  // Filtering client-side: the API already returns the tenant's whole library, so
  // typing shouldn't cost a round trip per keystroke. Matches the label and the
  // question id, which is what people actually search by.
  const needle = search.trim().toLowerCase();
  const visible = needle
    ? assets.filter((a) => {
      const label = prettyLabel(a).toLowerCase();
      // The file name is the only place the UUID appears, which is what someone
      // pastes when they are chasing down a specific URL.
      const fileName = (a.fileName || '').toLowerCase();
      const questionId = a.questionId ? String(a.questionId) : '';
      // "q169" as well as "169", because the chip in the listing shows the q form.
      return label.includes(needle) || fileName.includes(needle)
        || questionId === needle || ('q' + questionId) === needle;
    })
    : assets;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5">Media Library</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a video or audio clip, then copy its URL into a question.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canUpload && (
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={openUploadDialog}>
              Upload
            </Button>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={loadAssets} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Files uploaded here are stored on a <strong>public</strong> bucket &mdash; anyone with the
        link can watch or download them, and they cannot be made private later. Use this for
        question clips only, never for lecture recordings.
      </Alert>

      <TextField
        size="small"
        fullWidth
        placeholder="Search by label, file name or question ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This library is large enough that only the most recent files are shown.
        </Alert>
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={loadAssets}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={90}>Question</TableCell>
                <TableCell>Label</TableCell>
                <TableCell width={90}>Type</TableCell>
                <TableCell width={100}>Size</TableCell>
                <TableCell width={180}>Uploaded</TableCell>
                <TableCell width={250} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && !loadError && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {assets.length === 0
                        ? 'Nothing uploaded yet. Use the Upload button to add your first clip.'
                        : 'No files match that search.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {visible.map((asset) => (
                <React.Fragment key={asset.key}>
                  <TableRow hover>
                    <TableCell>
                      {asset.questionId ? (
                        <Chip size="small" color="primary" variant="outlined" label={'Q' + asset.questionId} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      {prettyLabel(asset)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={extensionOf(asset.fileName).toUpperCase()} />
                    </TableCell>
                    <TableCell>{formatSize(asset.sizeBytes)}</TableCell>
                    <TableCell>{formatDate(asset.lastModified)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={previewKey === asset.key ? 'Hide preview' : 'Preview'}>
                        <IconButton
                          size="small"
                          onClick={() => setPreviewKey(previewKey === asset.key ? null : asset.key)}
                        >
                          <PlayCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy URL">
                        <IconButton size="small" onClick={() => handleCopyUrl(asset)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy embed HTML">
                        <IconButton size="small" onClick={() => handleCopyEmbed(asset)}>
                          <CodeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Edit question ID and label">
                          <IconButton size="small" onClick={() => openEditDialog(asset)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => openDeleteDialog(asset)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>

                  {previewKey === asset.key && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ backgroundColor: 'action.hover' }}>
                        {isAudio(asset.fileName) ? (
                          <audio src={asset.url} controls preload="metadata" style={{ width: '100%' }}>
                            <track kind="captions" />
                          </audio>
                        ) : (
                          <video
                            src={asset.url}
                            controls
                            playsInline
                            preload="metadata"
                            style={{ maxWidth: '100%', maxHeight: 360, display: 'block' }}
                          >
                            <track kind="captions" />
                          </video>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}
                        >
                          {asset.url}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---- Upload dialog ---- */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Upload media
          <IconButton size="small" onClick={() => setUploadOpen(false)} disabled={uploading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The question ID and label can be changed later from the list. The file itself
            cannot be renamed &mdash; its URL is permanent, which is what keeps questions
            already using it working.
          </Typography>

          <TextField
            label="Question ID"
            placeholder="e.g. 132"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            value={uploadQuestionId}
            onChange={(e) => setUploadQuestionId(e.target.value.replace(/[^0-9]/g, ''))}
            helperText="Optional. Leave blank if the question doesn't exist yet."
            disabled={uploading}
          />

          <TextField
            label="Label"
            placeholder="e.g. webbed neck clinical clip"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            value={uploadLabel}
            onChange={(e) => setUploadLabel(e.target.value)}
            helperText="Shown in this list. Defaults to the file name."
            disabled={uploading}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.map((e) => '.' + e).join(',')}
            style={{ display: 'none' }}
            onChange={(e) => chooseFile(e.target.files && e.target.files[0])}
          />

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
          >
            {uploadFileObj ? 'Choose a different file' : 'Choose file'}
          </Button>

          {uploadFileObj && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {uploadFileObj.name} &middot; {formatSize(uploadFileObj.size)}
            </Typography>
          )}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadPercent} />
              <Typography variant="caption" color="text.secondary">{uploadPercent}%</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={submitUpload} disabled={!uploadFileObj || uploading}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Edit dialog ---- */}
      <Dialog open={!!editTarget} onClose={() => !saving && setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit details
          <IconButton size="small" onClick={() => setEditTarget(null)} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>
            Only what is shown in this list changes. The file&rsquo;s URL stays exactly the same,
            so every question already using it keeps working.
          </Alert>

          <TextField
            label="Question ID"
            placeholder="e.g. 132"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            value={editQuestionId}
            onChange={(e) => setEditQuestionId(e.target.value.replace(/[^0-9]/g, ''))}
            helperText="Optional. Clear it to detach the clip from a question. Must be a question that exists."
            disabled={saving}
          />

          <TextField
            label="Label"
            placeholder="e.g. webbed neck clinical clip"
            fullWidth
            size="small"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            helperText="Required. Shown in this list."
            disabled={saving}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 2, wordBreak: 'break-all' }}
          >
            File: {editTarget?.fileName}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={saving || !editLabel.trim()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete confirmation ---- */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete this file?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{deleteTarget ? prettyLabel(deleteTarget) : ''}</strong>
            {deleteTarget?.questionId ? ` (Q${deleteTarget.questionId})` : ''}
          </Typography>

          {usage.loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Checking where this file is used…</Typography>
            </Box>
          )}

          {!usage.loading && usage.error && (
            <Alert severity="warning">
              {usage.error} Deleting now could break questions without warning.
            </Alert>
          )}

          {!usage.loading && !usage.error && usage.questionIds.length > 0 && (
            <Alert severity="error">
              This file is used in {usage.questionIds.length} question
              {usage.questionIds.length === 1 ? '' : 's'}: {usage.questionIds.join(', ')}.
              Deleting it leaves a broken player in {usage.questionIds.length === 1 ? 'that question' : 'those questions'}.
            </Alert>
          )}

          {!usage.loading && !usage.error && usage.questionIds.length === 0 && (
            <Alert severity="info">
              No questions appear to use this file.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            This cannot be undone. The file is removed from S3 permanently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting || usage.loading}
          >
            {deleting ? 'Deleting…' : usage.questionIds.length > 0 ? 'Delete anyway' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
